import { createTheme, Theme } from '@mui/material/styles';
import { PaletteMode } from '@mui/material';

// design-system unit (2026-06-10、Option C ピボット):
// - 当初 experimental_extendTheme + CssVarsProvider を採用したが MUI v5.14.3 で
//   legacy コンポーネント (AppBar 等) の palette.mode 直参照と非互換が判明 (160/294 失敗)
// - createTheme + mode 切替で再構築、MUI v6 アップグレード時に CssVarsProvider 再挑戦
// - brand トークンは sx 値で直接参照、CSS variables は使わない (LP 内で oklch 直書き)
// - typography に Zen Kaku Gothic New を採用 (Google Fonts CDN 経由)

// brand トークン (LP 装飾用、Light/Dark 同値で一貫)
const brandTokens = {
  blue: 'oklch(0.68 0.16 252)',
  violet: 'oklch(0.66 0.18 296)',
  cyan: 'oklch(0.78 0.12 214)',
  amber: 'oklch(0.80 0.15 72)',
};

export const createAppTheme = (mode: PaletteMode): Theme =>
  createTheme({
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            primary: { main: '#1976d2' },
            secondary: { main: '#dc004e' },
            background: { default: '#fafafa', paper: '#ffffff' },
            text: {
              primary: 'rgba(0, 0, 0, 0.87)',
              secondary: 'rgba(0, 0, 0, 0.6)',
              disabled: 'rgba(0, 0, 0, 0.38)',
            },
            divider: 'rgba(0, 0, 0, 0.12)',
            brand: brandTokens,
          }
        : {
            primary: { main: '#90caf9' },
            secondary: { main: '#f48fb1' },
            // MUI v5 の color parser は oklch を解釈できないため、canvas で計算した rgb で固定する
            // (元の oklch 値は brandTokens 同様のトーン: 264hue 基調のダークブルー)
            background: {
              default: '#090f1c',
              paper: '#111828',
            },
            text: {
              primary: '#f2f5fb',
              secondary: '#abb1be',
              disabled: '#7e8696',
            },
            divider: 'rgba(255, 255, 255, 0.12)',
            brand: brandTokens,
          }),
    } as any,  // palette.brand カスタムキーは types/mui-theme.d.ts で augment 済、入力型は as any で通過
    typography: {
      fontFamily: '"Zen Kaku Gothic New", system-ui, sans-serif',
    },
    components: {
      // グローバルなテキスト状態色をテーマに集約 (未定義だとブラウザ既定色がダーク UI で浮く・濁る)。
      MuiCssBaseline: {
        styleOverrides: {
          // テキスト選択ハイライト: primary を半透明で敷き、文字色は据え置き
          // → 読みやすさを保ったまま見出し選択時のにじみを解消。::-moz-* で Firefox も対応。
          '::selection': {
            backgroundColor:
              mode === 'light' ? 'rgba(25, 118, 210, 0.28)' : 'rgba(144, 202, 249, 0.32)',
          },
          '::-moz-selection': {
            backgroundColor:
              mode === 'light' ? 'rgba(25, 118, 210, 0.28)' : 'rgba(144, 202, 249, 0.32)',
          },
          // プレースホルダ: ダークで薄すぎ/濃すぎを避け、Firefox の既定 opacity:0.54 も打ち消す
          '::placeholder': {
            color: mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.5)',
            opacity: 1,
          },
          // ブラウザ自動入力: Chrome の黄色背景がダーク UI を破壊するのを inset shadow で上書き
          'input:-webkit-autofill, input:-webkit-autofill:hover, input:-webkit-autofill:focus, textarea:-webkit-autofill, select:-webkit-autofill':
            {
              WebkitBoxShadow: `0 0 0 1000px ${mode === 'light' ? '#ffffff' : '#111828'} inset`,
              WebkitTextFillColor: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : '#f2f5fb',
              caretColor: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : '#f2f5fb',
              transition: 'background-color 5000s ease-in-out 0s',
            },
          // スクロールバー: ダークで明色バーが浮かないようテーマ色に (Webkit + Firefox)
          '*': {
            scrollbarColor:
              mode === 'light'
                ? 'rgba(0, 0, 0, 0.3) rgba(0, 0, 0, 0.05)'
                : 'rgba(255, 255, 255, 0.25) rgba(255, 255, 255, 0.05)',
          },
          '::-webkit-scrollbar': { width: 12, height: 12 },
          '::-webkit-scrollbar-track': {
            backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.05)' : 'rgba(255, 255, 255, 0.05)',
          },
          '::-webkit-scrollbar-thumb': {
            backgroundColor: mode === 'light' ? 'rgba(0, 0, 0, 0.3)' : 'rgba(255, 255, 255, 0.25)',
            borderRadius: 6,
          },
          // キーボードフォーカスリング: ダーク背景でも視認できる primary 色で統一 (WCAG 2.4.7)
          ':focus-visible': {
            outline: `2px solid ${mode === 'light' ? '#1976d2' : '#90caf9'}`,
            outlineOffset: 2,
          },
        },
      },
    },
  });

// 既存テスト / 後方互換のため、デフォルト export は light theme の固定インスタンス
const theme = createAppTheme('light');

export default theme;
